/**
 * `src/lib/ui/reader/surahs.ts` — the 114 surah names, in Arabic.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  ⚠ PROVISIONAL. THIS TABLE IS A KNOWN OPEN DECISION, NOT A FINISHED ANSWER.
 * ═══════════════════════════════════════════════════════════════════════════
 * `M1-STATUS.md` §7(b) is explicit:
 *
 *   "Settle where surah names come from. `meta.json` emits
 *    `_deferred.surah_names` saying so out loud: the vendored file contains no
 *    names, no revelation place and no revelation order, and no source for them
 *    is pinned in `sources/SOURCES.json`. […] Where they come from is a SPEC
 *    §2.2 content-sources decision with a `licences.yml` entry behind it — not
 *    something for a component to hard-code."
 *
 * M2 could not ship a reader without them — a surah index reading
 * "سورة ٦٧" is not a navigable mushaf — so they are here, in ONE file, marked,
 * under three rules that make the eventual move cheap and safe:
 *
 *   1. DISPLAY ONLY. Nothing in this project may derive a `ref`, a `Pos`, an
 *      `ayahId`, a word index or any stored value from this table. It is read
 *      by headings and by the surah index and by nothing else. Delete it and
 *      the anchor space is untouched.
 *   2. THE STRUCTURE IS NOT HERE. Ayah counts, first/last `ayahId`, juz and the
 *      basmala flag all come from `meta.json`'s `surahs` table and from the
 *      frozen `SURAH_AYAH_COUNT`. `reader.spec.ts` asserts this file agrees
 *      with the frozen offsets on the ONE structural fact it repeats (114
 *      rows, numbered 1..114 in order) and carries nothing else.
 *   3. NO REVELATION PLACE, NO REVELATION ORDER. Both are editorial claims with
 *      genuine scholarly disagreement at the margins (al-Fatiha, al-Qadr,
 *      ar-Ra'd, …). SPEC §1.5 Gate 3's posture — a missing grading is a build
 *      failure, not a pass — applies to this project's attitude to unsourced
 *      religious data generally. A "مكية" badge sourced from nothing is exactly
 *      the silent, confident, unattributed claim the pipeline gates exist to
 *      refuse. When §7(b) is answered with a pinned source, the badge arrives
 *      with it.
 *
 * ON RIGHTS, so the §7(b) conversation starts from the right place: the names
 * themselves are not the concern. A chapter name is a fact and a 1400-year-old
 * one; `licences.yml` has nothing to record about "البقرة". What §7(b) is
 * actually asking for is PROVENANCE — a pinned, checksummed source so that this
 * table is verified rather than typed, the way every other string in
 * `static/content/` is. That is a pipeline change, and it belongs to whoever
 * owns `pipeline/` — not to this file.
 */

/**
 * Surah names, index 0 unused so that `SURAH_NAMES[n]` is surah `n`. The
 * padding matches `AYAH_OFFSET`'s 115-entry convention in the frozen anchor
 * module, so the two tables are indexed the same way.
 */
export const SURAH_NAMES: readonly string[] = [
	'',
	'الفاتحة',
	'البقرة',
	'آل عمران',
	'النساء',
	'المائدة',
	'الأنعام',
	'الأعراف',
	'الأنفال',
	'التوبة',
	'يونس',
	'هود',
	'يوسف',
	'الرعد',
	'إبراهيم',
	'الحجر',
	'النحل',
	'الإسراء',
	'الكهف',
	'مريم',
	'طه',
	'الأنبياء',
	'الحج',
	'المؤمنون',
	'النور',
	'الفرقان',
	'الشعراء',
	'النمل',
	'القصص',
	'العنكبوت',
	'الروم',
	'لقمان',
	'السجدة',
	'الأحزاب',
	'سبأ',
	'فاطر',
	'يس',
	'الصافات',
	'ص',
	'الزمر',
	'غافر',
	'فصلت',
	'الشورى',
	'الزخرف',
	'الدخان',
	'الجاثية',
	'الأحقاف',
	'محمد',
	'الفتح',
	'الحجرات',
	'ق',
	'الذاريات',
	'الطور',
	'النجم',
	'القمر',
	'الرحمن',
	'الواقعة',
	'الحديد',
	'المجادلة',
	'الحشر',
	'الممتحنة',
	'الصف',
	'الجمعة',
	'المنافقون',
	'التغابن',
	'الطلاق',
	'التحريم',
	'الملك',
	'القلم',
	'الحاقة',
	'المعارج',
	'نوح',
	'الجن',
	'المزمل',
	'المدثر',
	'القيامة',
	'الإنسان',
	'المرسلات',
	'النبأ',
	'النازعات',
	'عبس',
	'التكوير',
	'الانفطار',
	'المطففين',
	'الانشقاق',
	'البروج',
	'الطارق',
	'الأعلى',
	'الغاشية',
	'الفجر',
	'البلد',
	'الشمس',
	'الليل',
	'الضحى',
	'الشرح',
	'التين',
	'العلق',
	'القدر',
	'البينة',
	'الزلزلة',
	'العاديات',
	'القارعة',
	'التكاثر',
	'العصر',
	'الهمزة',
	'الفيل',
	'قريش',
	'الماعون',
	'الكوثر',
	'الكافرون',
	'النصر',
	'المسد',
	'الإخلاص',
	'الفلق',
	'الناس'
];

/** The number of surahs this table names. Cross-checked against the frozen
 *  `SURAH_COUNT` in `reader.spec.ts` rather than restated from memory. */
export const NAMED_SURAHS = SURAH_NAMES.length - 1;

/**
 * The display name of a surah, or `'سورة ٧'`-style fallback for an out-of-range
 * number. Total: this is called from a heading, and a heading must render.
 */
export function surahName(surah: number): string {
	const name = SURAH_NAMES[surah];
	return name === undefined || name === '' ? '' : name;
}
